import React, { useState, useCallback } from 'react';
import Welcome from './components/Welcome';
import DocumentEditor from './components/DocumentEditor';
import Chat from './components/Chat';
import { ChatMessage, DocumentStructure } from './services/openRouterService';
import openRouterService from './services/openRouterService';
import './App.css';

type AppMode = 'welcome' | 'editor';

interface SectionProgress {
  title: string;
  level: number;
  status: 'pending' | 'generating' | 'completed';
  subsections?: SectionProgress[];
}

const App: React.FC = () => {
  const [mode, setMode] = useState<AppMode>('welcome');
  const [documentContent, setDocumentContent] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');

  const systemPrompt = `You are a Senior Solution Architect with 15+ years of experience in business strategy, technical architecture, and executive communication. Your expertise spans multiple industries and you have a proven track record of creating world-class documentation that impresses senior executives and technical stakeholders.

When creating documents:
1. Use professional, executive-level language and tone
2. Demonstrate deep industry expertise and strategic thinking
3. Provide substantial, meaningful content (not superficial)
4. Include relevant examples, case studies, and data points
5. Use proper HTML formatting for professional presentation
6. Focus on business value, ROI, and actionable insights
7. Maintain consistency with high-quality professional standards
8. CRITICAL: Use proper HTML formatting (h1, h2, h3, p, ul, ol, li, strong, em, table, thead, tbody, tr, td, th)
9. CRITICAL: Do NOT repeat section headings or titles
10. CRITICAL: For tables, use proper HTML structure with <table>, <thead>, <tbody>, <tr>, <th>, <td> tags

When chatting with users:
1. Be helpful and professional, maintaining your expert persona
2. Provide strategic insights and professional recommendations
3. Answer questions with depth and expertise
4. Help refine and enhance existing content to executive standards
5. When asked to modify documents, provide comprehensive, high-quality updates with proper HTML formatting

Always maintain the persona of a Senior Solution Architect who creates documents that would impress C-level executives and technical stakeholders.`;

  // Convert document structure to progress tracking
  const createSectionProgress = useCallback((structure: DocumentStructure): SectionProgress[] => {
    return structure.sections.map(section => ({
      title: section.title,
      level: section.level,
      status: 'pending',
      subsections: section.subsections?.map(subsection => ({
        title: subsection.title,
        level: subsection.level,
        status: 'pending'
      }))
    }));
  }, []);

  // Generate document index message for chat
  const generateIndexMessage = useCallback((structure: DocumentStructure, progress: SectionProgress[]): string => {
    let indexText = `📋 **Document Structure: ${structure.title}**\n\n`;
    
    progress.forEach((section, sectionIndex) => {
      const statusIcon = section.status === 'completed' ? '✅' : 
                        section.status === 'generating' ? '🔄' : '⏳';
      const indent = '  '.repeat(section.level - 1);
      indexText += `${indent}${statusIcon} **${section.title}**\n`;
      
      if (section.subsections) {
        section.subsections.forEach((subsection, subsectionIndex) => {
          const subStatusIcon = subsection.status === 'completed' ? '✅' : 
                               subsection.status === 'generating' ? '🔄' : '⏳';
          const subIndent = '  '.repeat(subsection.level - 1);
          indexText += `${subIndent}${subStatusIcon} ${subsection.title}\n`;
        });
      }
    });
    
    return indexText;
  }, []);

  // Generate the complete document index as HTML
  const generateDocumentIndexHTML = useCallback((structure: DocumentStructure): string => {
    let indexHTML = `<h1>${structure.title}</h1>\n\n`;
    indexHTML += `<h2>Table of Contents</h2>\n<ul>\n`;
    
    structure.sections.forEach((section) => {
      indexHTML += `<li><strong>${section.title}</strong>\n`;
      
      if (section.subsections) {
        indexHTML += `<ul>\n`;
        section.subsections.forEach((subsection) => {
          indexHTML += `<li>${subsection.title}</li>\n`;
        });
        indexHTML += `</ul>\n`;
      }
      
      indexHTML += `</li>\n`;
    });
    
    indexHTML += `</ul>\n\n`;
    return indexHTML;
  }, []);

  // Agent-based structured document generation with enhanced context
  const generateStructuredDocument = useCallback(async (prompt: string) => {
    try {
      // Step 1: Generate document structure
      setGenerationProgress('Creating sophisticated document structure...');
      const structure = await openRouterService.generateDocumentStructure(prompt);
      
      // Step 2: Analyze document context
      const context = await openRouterService.analyzeDocumentContext(prompt, '');
      
      // Initialize progress tracking
      const progress = createSectionProgress(structure);
      
      // Step 3: Add complete document index to editor first
      const documentIndexHTML = generateDocumentIndexHTML(structure);
      setDocumentContent(documentIndexHTML);
      
      // Add structure to chat with context
      const indexMessage = generateIndexMessage(structure, progress);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant' as const, content: `🎯 **Solution Architect Analysis Complete**\n\n**Document Type:** ${context.documentType}\n**Target Audience:** ${context.targetAudience}\n**Objective:** ${context.objective}\n\n${indexMessage}` }
      ]);
      
      // Step 4: Generate content for each section with enhanced context (without repeating titles)
      let fullContent = documentIndexHTML;
      
      for (let i = 0; i < structure.sections.length; i++) {
        const section = structure.sections[i];
        setGenerationProgress(`Generating section ${i + 1}/${structure.sections.length}: ${section.title}`);
        
        // Update progress to generating
        progress[i].status = 'generating';
        
        // Update chat with progress
        const updatedIndexMessage = generateIndexMessage(structure, progress);
        setChatMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = { 
            role: 'assistant' as const, 
            content: `🎯 **Solution Architect Analysis Complete**\n\n**Document Type:** ${context.documentType}\n**Target Audience:** ${context.targetAudience}\n**Objective:** ${context.objective}\n\n${updatedIndexMessage}` 
          };
          return newMessages;
        });
        
        // Update context with current section
        const updatedContext = {
          ...context,
          currentSection: section.title,
          completedSections: structure.sections.slice(0, i).map(s => s.title),
          documentSummary: `Generated ${i} sections, working on ${section.title}`
        };
        
        // Generate main section content with enhanced context (without title)
        const sectionContent = await openRouterService.generateSectionContent(
          section,
          updatedContext,
          fullContent
        );
        
        // Add section content without repeating the title
        fullContent += `<h2>${section.title}</h2>\n${sectionContent}\n\n`;
        setDocumentContent(fullContent);
        progress[i].status = 'completed';
        
        // Generate subsections if they exist
        if (section.subsections) {
          for (let j = 0; j < section.subsections.length; j++) {
            const subsection = section.subsections[j];
            setGenerationProgress(`Generating subsection ${j + 1}/${section.subsections.length}: ${subsection.title}`);
            
            // Update subsection progress to generating
            progress[i].subsections![j].status = 'generating';
            
            // Update chat with subsection progress
            const subsectionIndexMessage = generateIndexMessage(structure, progress);
            setChatMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = { 
                role: 'assistant' as const, 
                content: `🎯 **Solution Architect Analysis Complete**\n\n**Document Type:** ${context.documentType}\n**Target Audience:** ${context.targetAudience}\n**Objective:** ${context.objective}\n\n${subsectionIndexMessage}` 
              };
              return newMessages;
            });
            
            // Update context for subsection
            const subsectionContext = {
              ...updatedContext,
              currentSection: subsection.title,
              documentSummary: `Generated ${i} sections with ${j} subsections, working on ${subsection.title}`
            };
            
            const subsectionContent = await openRouterService.generateSectionContent(
              subsection,
              subsectionContext,
              fullContent
            );
            
            // Add subsection content without repeating the title
            fullContent += `<h3>${subsection.title}</h3>\n${subsectionContent}\n\n`;
            setDocumentContent(fullContent);
            progress[i].subsections![j].status = 'completed';
          }
        }
        
        // Update final progress for this section
        const finalIndexMessage = generateIndexMessage(structure, progress);
        setChatMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = { 
            role: 'assistant' as const, 
            content: `🎯 **Solution Architect Analysis Complete**\n\n**Document Type:** ${context.documentType}\n**Target Audience:** ${context.targetAudience}\n**Objective:** ${context.objective}\n\n${finalIndexMessage}` 
          };
          return newMessages;
        });
      }
      
      setGenerationProgress('Document generation complete!');
      
      // Add completion message
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant' as const, content: '🎉 **Executive-Ready Document Complete!**\n\nI\'ve created a comprehensive, professional document that demonstrates strategic thinking and technical excellence. The document is structured for executive presentation and includes substantial, actionable content. You can now review, edit, and continue our conversation to make any refinements.' }
      ]);
      
      return fullContent;
    } catch (error) {
      console.error('Error in structured generation:', error);
      throw error;
    }
  }, [createSectionProgress, generateIndexMessage, generateDocumentIndexHTML]);

  const handleStartDocument = useCallback(async (prompt: string, useDeepResearch: boolean) => {
    setIsLoading(true);
    setIsGenerating(true);
    setGenerationProgress('');
    
    // Switch to editor immediately
    setMode('editor');
    setDocumentContent('');
    
    // Add initial user message to chat
    setChatMessages([{ role: 'user' as const, content: prompt }]);
    
    try {
      console.log('Starting document generation with prompt:', prompt);
      console.log('Using DeepResearch:', useDeepResearch);
      
      if (useDeepResearch) {
        // DeepResearch approach
        setGenerationProgress('Starting comprehensive DeepResearch analysis...');
        setChatMessages(prev => [
          ...prev,
          { role: 'assistant' as const, content: '🔍 **Senior Solution Architect - DeepResearch Mode Activated**\n\nI\'m conducting comprehensive research and analysis to create a world-class, executive-ready document. This will include strategic thinking, technical excellence, and actionable insights suitable for C-level executives.\n\nThis process may take a few moments as I ensure the highest quality output...' }
        ]);
        
        await openRouterService.generateDeepResearchDocument(
          prompt,
          (progress: string) => {
            setDocumentContent(progress);
          }
        );
        
        // Add completion message for DeepResearch
        setChatMessages(prev => [
          ...prev,
          { role: 'assistant' as const, content: '🎉 **World-Class Document Complete!**\n\nI\'ve created a comprehensive, multi-page document that demonstrates strategic thinking, technical excellence, and executive communication. The document includes substantial content, professional analysis, and actionable recommendations suitable for senior executives and technical stakeholders.' }
        ]);
      } else {
        // Structured generation approach
        await generateStructuredDocument(prompt);
      }

    } catch (error: any) {
      console.error('Error generating document:', error);
      alert(`Error generating document: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
      setGenerationProgress('');
    }
  }, [generateStructuredDocument]);

  const handleSendMessage = useCallback(async (message: string) => {
    const newMessages = [...chatMessages, { role: 'user' as const, content: message }];
    setChatMessages(newMessages);
    setIsLoading(true);

    try {
      // Check if the message is asking to modify the document
      const isModificationRequest = 
        message.toLowerCase().includes('modify') || 
        message.toLowerCase().includes('change') || 
        message.toLowerCase().includes('update') ||
        message.toLowerCase().includes('edit') ||
        message.toLowerCase().includes('add') ||
        message.toLowerCase().includes('include') ||
        message.toLowerCase().includes('expand') ||
        message.toLowerCase().includes('improve') ||
        message.toLowerCase().includes('enhance');

      if (isModificationRequest) {
        // Handle document modification with streaming
        setIsGenerating(true);
        
        const modifyMessages: ChatMessage[] = [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: `Current document content: ${documentContent}. User request: ${message}. Please provide the complete updated document content with HTML formatting based on the user's request. Maintain the high quality and professional standards of a Senior Solution Architect.` }
        ];

        let updatedContent = '';
        
        await openRouterService.streamChatCompletion(
          modifyMessages,
          'quality',
          (chunk: string) => {
            updatedContent += chunk;
            setDocumentContent(updatedContent);
          },
          4000
        );
        
        // Add assistant response to chat
        setChatMessages(prev => [
          ...prev,
          { role: 'assistant' as const, content: '✅ **Document Updated Successfully**\n\nI\'ve enhanced the document based on your request, maintaining the high quality and professional standards expected of executive-level documentation. The changes have been applied to the editor.' }
        ]);
        
        setIsGenerating(false);
      } else {
        // Handle regular chat conversation
        const messages: ChatMessage[] = [
          { role: 'system', content: systemPrompt },
          ...newMessages
        ];

        const response = await openRouterService.chatCompletion(
          messages,
          'balanced',
          2000
        );

        const assistantMessage = response.choices[0].message.content;
        setChatMessages([...newMessages, { role: 'assistant' as const, content: assistantMessage }]);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      alert(`Error sending message: ${error.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [chatMessages, documentContent, systemPrompt]);

  const handleNewDocument = useCallback(() => {
    setMode('welcome');
    setDocumentContent('');
    setChatMessages([]);
    setIsGenerating(false);
    setGenerationProgress('');
  }, []);

  const handleContentChange = useCallback((content: string) => {
    setDocumentContent(content);
  }, []);

  return (
    <div className="app">
      {mode === 'welcome' ? (
        <Welcome onStartDocument={handleStartDocument} isLoading={isLoading} />
      ) : (
        <div className="editor-layout">
          <div className="editor-panel">
            <DocumentEditor
              content={documentContent}
              onContentChange={handleContentChange}
              isGenerating={isGenerating}
              generationProgress={generationProgress}
            />
          </div>
          <div className="chat-panel">
            <Chat
              messages={chatMessages}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              onNewDocument={handleNewDocument}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default App;

